/**
 * MM Product Zoom
 * @description Ouvre les médias du slider produit en plein écran (lightbox verticale)
 *              avec fondu et scroll fluide vers l'image cliquée.
 * @version 1.0.0
 */

const MM_ProductZoom = (() => {
	'use strict';

	// ==========================================================================
	// Configuration
	// ==========================================================================
	const CONFIG = {
		isDevelopment:
			window.location.hostname === 'localhost' ||
			window.location.hostname.includes('.myshopify.com'),
		selectors: {
			slider: '.main-slider',
			// Slides réels (hors clones Splide) triés par data-index
			realSlide: '.splide__slide:not(.splide__slide--clone)',
			media: 'img, video',
		},
		classes: {
			overlay: 'mm-product-zoom',
			scroll: 'mm-product-zoom__scroll',
			inner: 'mm-product-zoom__inner',
			item: 'mm-product-zoom__item',
			close: 'mm-product-zoom__close',
			active: 'mm-active',
			bodyLock: 'mm-modal-open',
		},
		// Distance max (px) entre pointerdown et click pour distinguer clic/drag
		DRAG_THRESHOLD: 8,
		// Délai avant le scroll fluide, pour laisser le fondu démarrer
		SCROLL_DELAY: 200,
	};

	// ==========================================================================
	// State
	// ==========================================================================
	let overlay = null;
	let scrollEl = null;
	let triggerElement = null;
	let pointerStart = null;

	// ==========================================================================
	// Logging
	// ==========================================================================
	const log = (level, message, data = {}) => {
		if (!CONFIG.isDevelopment && level === 'info') return;
		const logFn = console[level] || console.log;
		logFn(`[MM_ProductZoom] ${message}`, data);
	};

	// ==========================================================================
	// Récupération des médias (ordre du slider)
	// ==========================================================================
	function getOrderedMedia(slider) {
		const slides = Array.from(
			slider.querySelectorAll(CONFIG.selectors.realSlide)
		).sort(
			(a, b) =>
				(parseInt(a.dataset.index, 10) || 0) -
				(parseInt(b.dataset.index, 10) || 0)
		);

		return slides
			.map((slide) => slide.querySelector(CONFIG.selectors.media))
			.filter(Boolean);
	}

	// ==========================================================================
	// Construction de l'overlay
	// ==========================================================================
	function buildOverlay(mediaList, closeLabel) {
		overlay = document.createElement('div');
		overlay.className = CONFIG.classes.overlay;
		overlay.setAttribute('role', 'dialog');
		overlay.setAttribute('aria-modal', 'true');

		// Bouton fermer
		const closeBtn = document.createElement('button');
		closeBtn.type = 'button';
		closeBtn.className = CONFIG.classes.close;
		closeBtn.setAttribute('aria-label', closeLabel || 'Close');
		closeBtn.innerHTML = `
			<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" aria-hidden="true">
				<path d="M6 6 L18 18 M18 6 L6 18" />
			</svg>`;
		closeBtn.addEventListener('click', close);

		// Zone scrollable
		scrollEl = document.createElement('div');
		scrollEl.className = CONFIG.classes.scroll;
		scrollEl.addEventListener('click', close);

		const inner = document.createElement('div');
		inner.className = CONFIG.classes.inner;

		mediaList.forEach((media) => {
			const item = document.createElement('div');
			item.className = CONFIG.classes.item;

			const clone = media.cloneNode(true);
			if (clone.tagName === 'IMG') {
				clone.loading = 'eager';
				clone.removeAttribute('fetchpriority');
				clone.sizes = '(max-width: 980px) 100vw, 980px';
			}
			item.appendChild(clone);
			inner.appendChild(item);
		});

		scrollEl.appendChild(inner);
		overlay.appendChild(closeBtn);
		overlay.appendChild(scrollEl);
		document.body.appendChild(overlay);
	}

	// ==========================================================================
	// Open / Close
	// ==========================================================================
	function open(slider, clickedIndex, trigger) {
		if (overlay) return;

		const mediaList = getOrderedMedia(slider);
		if (!mediaList.length) return;

		triggerElement = trigger || null;
		buildOverlay(mediaList, slider.dataset.zoomCloseLabel);

		document.body.classList.add(CONFIG.classes.bodyLock);
		document.addEventListener('keydown', handleEscape);

		// Fondu d'entrée (frame suivante pour déclencher la transition CSS)
		requestAnimationFrame(() => {
			overlay?.classList.add(CONFIG.classes.active);
		});

		// Scroll fluide vers l'image cliquée
		const items = scrollEl.querySelectorAll(`.${CONFIG.classes.item}`);
		const target = items[clickedIndex];
		if (target) {
			setTimeout(() => {
				scrollEl?.scrollTo({ top: target.offsetTop, behavior: 'smooth' });
			}, CONFIG.SCROLL_DELAY);
		}

		log('info', 'Opened', { count: mediaList.length, clickedIndex });
	}

	function close() {
		if (!overlay) return;

		const el = overlay;
		el.classList.remove(CONFIG.classes.active);

		document.body.classList.remove(CONFIG.classes.bodyLock);
		document.removeEventListener('keydown', handleEscape);

		// Retirer après le fondu de sortie
		const removeOverlay = () => {
			el.remove();
			if (overlay === el) {
				overlay = null;
				scrollEl = null;
			}
		};
		// Ignorer les transitionend qui remontent des enfants (ex. hover bouton)
		const onTransitionEnd = (e) => {
			if (e.target !== el || e.propertyName !== 'opacity') return;
			el.removeEventListener('transitionend', onTransitionEnd);
			removeOverlay();
		};
		el.addEventListener('transitionend', onTransitionEnd);
		setTimeout(removeOverlay, 700); // filet de sécurité

		if (triggerElement && typeof triggerElement.focus === 'function') {
			triggerElement.focus();
		}
		triggerElement = null;
	}

	// ==========================================================================
	// Event handlers
	// ==========================================================================
	function handleEscape(e) {
		if (e.key === 'Escape') close();
	}

	function handlePointerDown(e) {
		pointerStart = { x: e.clientX, y: e.clientY };
	}

	function handleClick(e) {
		if (overlay) return; // clics dans l'overlay gérés séparément

		const img = e.target.closest(
			`${CONFIG.selectors.slider} .splide__slide img`
		);
		if (!img) return;

		// Ignorer si c'était un drag du slider
		if (pointerStart) {
			const dx = Math.abs(e.clientX - pointerStart.x);
			const dy = Math.abs(e.clientY - pointerStart.y);
			pointerStart = null;
			if (dx > CONFIG.DRAG_THRESHOLD || dy > CONFIG.DRAG_THRESHOLD) return;
		}

		const slider = img.closest(CONFIG.selectors.slider);
		const slide = img.closest('.splide__slide');
		if (!slider || !slide) return;

		// Index de l'image cliquée dans l'ordre réel du slider
		const clickedIndex = parseInt(slide.dataset.index, 10) || 0;

		open(slider, clickedIndex, img);
	}

	// ==========================================================================
	// Init
	// ==========================================================================
	function init() {
		document.addEventListener('pointerdown', handlePointerDown);
		document.addEventListener('click', handleClick);
		log('info', 'Initialized');
	}

	// ==========================================================================
	// Public API
	// ==========================================================================
	return { init, open, close };
})();

// Auto-init on DOM ready
if (document.readyState === 'loading') {
	document.addEventListener('DOMContentLoaded', MM_ProductZoom.init);
} else {
	MM_ProductZoom.init();
}
